import { useState } from "react";
import { Link } from "react-router-dom";
import { forgotPassword } from "../services/authService";

// Deliberately shows the SAME message on every successful submission,
// straight from the backend's own generic response — this page never
// tries to infer or display "email exists"/"email not found" itself.
// The backend (POST /auth/forgot-password) is what actually enforces
// the anti-enumeration guarantee; this page just doesn't undermine it.
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const data = await forgotPassword(email.trim());
      setMessage(data?.message || "If an account exists for this email, you will receive a password reset link shortly.");
    } catch (err) {
      if (!err?.response) {
        setError("Unable to connect to the server. Please check your connection and try again.");
      } else {
        // A 429 (rate limited) or 422 (malformed email) message is safe to
        // show as-is — neither reveals whether the account exists.
        const msg = err.response.data?.detail;
        setError(typeof msg === "string" ? msg : "Unable to submit your request. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-sm mx-auto px-4 py-16">
      <h1 className="text-xl font-semibold text-neutral-900 mb-2 text-center">Forgot Password</h1>
      <p className="text-sm text-neutral-500 text-center mb-6">
        Enter the email address associated with your account.
      </p>

      {message ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-4 text-center">
          <p className="text-sm text-green-800">{message}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label htmlFor="fp-email" className="block text-sm font-medium text-neutral-700 mb-1">
              Email
            </label>
            <input
              id="fp-email"
              type="email"
              required
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>

          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-neutral-900 text-white text-sm font-medium rounded-lg px-4 py-3 hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? "Sending..." : "Send Reset Link"}
          </button>
        </form>
      )}

      <p className="text-sm text-neutral-500 text-center mt-6">
        <Link to="/login" className="text-neutral-900 underline">
          Back to Login
        </Link>
      </p>
    </div>
  );
}
